import { Fragment, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, RefreshCw, Search, Sparkles } from 'lucide-react';
import {
  DEFAULT_AUTHORITY_PROFILE,
  type ConnectionView,
  type IngestionRunView,
  type KnowledgeItemView,
  type KnowledgeSourceView,
  type SearchKnowledgeResult,
} from '@agentic-os/contracts';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, ErrorNote, Mono, PageHeader, Table, Td, Th } from '@/components/ui/primitives';
import { DomainStatus } from '@/components/ui/status-badge';
import { api, formatTime } from '@/lib/api';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/cn';

const input = 'w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm';
const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'] as const;
type Tab = 'sources' | 'search' | 'review';

/**
 * Knowledge (04-KNOWLEDGE-MEMORY): sources that sync incrementally, hybrid search with
 * citations, and the human review queue. "The AI suggested this" (CANDIDATE) is always shown
 * distinctly from "the organization knows this" (VALIDATED).
 */
export function KnowledgePage() {
  const [tab, setTab] = useState<Tab>('sources');
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Knowledge"
        description="What the organization knows: synced sources, cited search, and candidates waiting for human review."
      />
      <div role="tablist" className="flex gap-1 border-b border-border">
        {(
          [
            ['sources', 'Sources'],
            ['search', 'Search'],
            ['review', 'Review'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm',
              tab === k
                ? 'border-accent font-medium'
                : 'border-transparent text-muted hover:text-inherit',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'sources' && <SourcesTab />}
      {tab === 'search' && <SearchTab />}
      {tab === 'review' && <ReviewTab />}
    </div>
  );
}

function SourcesTab() {
  const { can } = useSession();
  const manage = can('knowledge.manage');
  const qc = useQueryClient();
  const sources = useQuery({
    queryKey: ['k-sources'],
    queryFn: () => api<{ items: KnowledgeSourceView[] }>('/api/v1/knowledge/sources'),
    refetchInterval: 10_000,
  });
  const conns = useQuery({
    queryKey: ['connections'],
    queryFn: () => api<{ items: ConnectionView[] }>('/api/v1/connections'),
    enabled: manage,
  });
  const docConns = (conns.data?.items ?? []).filter(
    (c) => c.bindings['page.changes'] && c.bindings['page.read'],
  );
  const empty = {
    name: '',
    connectionId: '',
    spaces: '',
    pageIds: '',
    includeChildren: true,
    authorityClass: 'CURRENT_DOC',
    classification: 'INTERNAL',
    scheduleMinutes: '',
    description: '',
    status: 'active',
  };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState<KnowledgeSourceView | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api(editing ? `/api/v1/knowledge/sources/${editing.id}` : '/api/v1/knowledge/sources', {
        method: editing ? 'PUT' : 'POST',
        body: {
          name: form.name,
          description: form.description,
          status: form.status,
          connectionId: form.connectionId,
          roots: {
            spaces: form.spaces
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
            pageIds: form.pageIds
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          },
          includeChildren: form.includeChildren,
          authorityClass: form.authorityClass,
          classification: form.classification,
          scheduleMinutes: form.scheduleMinutes ? Number(form.scheduleMinutes) : null,
        },
      }),
    onSuccess: () => {
      setForm(empty);
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['k-sources'] });
    },
  });
  /** Loads a source into the form (the name cannot change). */
  const startEdit = (s: KnowledgeSourceView) => {
    setEditing(s);
    create.reset();
    setForm({
      name: s.name,
      connectionId: s.connectionId,
      spaces: s.roots.spaces.join(', '),
      pageIds: s.roots.pageIds.join(', '),
      includeChildren: s.includeChildren,
      authorityClass: s.authorityClass,
      classification: s.classification,
      scheduleMinutes: s.scheduleMinutes === null ? '' : String(s.scheduleMinutes),
      description: s.description,
      status: s.status,
    });
    document.getElementById('source-form')?.scrollIntoView({ behavior: 'smooth' });
  };
  const cancelEdit = () => {
    setEditing(null);
    setForm(empty);
  };
  const sync = useMutation({
    mutationFn: (a: { id: string; fullResync: boolean }) =>
      api(`/api/v1/knowledge/sources/${a.id}/sync`, {
        method: 'POST',
        body: { fullResync: a.fullResync },
      }),
    onSuccess: (_d, a) => {
      setSelected(a.id);
      void qc.invalidateQueries({ queryKey: ['k-runs', a.id] });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <div className="space-y-6">
      {manage && (
        <Card id="source-form">
          <CardHeader>
            <CardTitle>
              {editing ? `Edit knowledge source ${editing.name}` : 'Add knowledge source'}
            </CardTitle>
          </CardHeader>
          <CardBody>
            <form onSubmit={submit} className="grid gap-3 md:grid-cols-4" autoComplete="off">
              <label className="text-xs text-muted">
                Name
                <input
                  className={input}
                  required
                  readOnly={!!editing}
                  pattern="[a-z0-9][a-z0-9._\-]{1,62}"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted">
                Document connection
                <select
                  className={input}
                  required
                  value={form.connectionId}
                  onChange={(e) => setForm({ ...form, connectionId: e.target.value })}
                >
                  <option value="">Choose…</option>
                  {docConns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.classification})
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-muted">
                Spaces (comma-separated)
                <input
                  className={input}
                  placeholder="ARCH"
                  value={form.spaces}
                  onChange={(e) => setForm({ ...form, spaces: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted">
                Root page ids (optional)
                <input
                  className={input}
                  value={form.pageIds}
                  onChange={(e) => setForm({ ...form, pageIds: e.target.value })}
                />
                <span className="mt-1 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.includeChildren}
                    onChange={(e) => setForm({ ...form, includeChildren: e.target.checked })}
                  />
                  Include child pages of the root pages
                </span>
              </label>
              <label className="text-xs text-muted">
                Authority class
                <select
                  className={input}
                  value={form.authorityClass}
                  onChange={(e) => setForm({ ...form, authorityClass: e.target.value })}
                >
                  {DEFAULT_AUTHORITY_PROFILE.map((a) => (
                    <option key={a}>{a}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-muted">
                Classification
                <select
                  className={input}
                  value={form.classification}
                  onChange={(e) => setForm({ ...form, classification: e.target.value })}
                >
                  {CLASSIFICATIONS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-muted">
                Sync every (minutes, optional)
                <input
                  className={input}
                  type="number"
                  min="15"
                  value={form.scheduleMinutes}
                  onChange={(e) => setForm({ ...form, scheduleMinutes: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted md:col-span-2">
                Description
                <input
                  className={input}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </label>
              {editing && (
                <label className="text-xs text-muted">
                  Status
                  <select
                    className={input}
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="active">active</option>
                    <option value="paused">paused</option>
                  </select>
                </label>
              )}
              <div className="flex flex-wrap items-end gap-2">
                <Button type="submit" variant="primary" disabled={create.isPending}>
                  <BookOpen className="size-3.5" aria-hidden />{' '}
                  {editing ? 'Save changes' : 'Save source'}
                </Button>
                {editing && <Button onClick={cancelEdit}>Cancel</Button>}
              </div>
              {editing && (
                <p className="text-xs text-muted md:col-span-4">
                  The name is fixed. Changing the connection or the roots resets the sync cursor:
                  the next sync is a full one.
                </p>
              )}
            </form>
            {create.isError && (
              <div className="mt-3">
                <ErrorNote error={create.error} />
              </div>
            )}
          </CardBody>
        </Card>
      )}
      {sources.isError && <ErrorNote error={sources.error} />}
      {sync.isError && <ErrorNote error={sync.error} />}
      <Card>
        <CardBody className="p-0">
          <Table>
            <thead>
              <tr>
                <Th>Source</Th>
                <Th>Connection · roots</Th>
                <Th>Authority</Th>
                <Th>Classification</Th>
                <Th className="text-right">Documents</Th>
                <Th>Last sync</Th>
                {manage && <Th>Actions</Th>}
              </tr>
            </thead>
            <tbody>
              {(sources.data?.items ?? []).map((s) => (
                <tr key={s.id}>
                  <Td>
                    <button
                      type="button"
                      className="text-accent hover:underline"
                      onClick={() => setSelected(s.id === selected ? null : s.id)}
                    >
                      {s.name}
                    </button>
                    {s.status === 'paused' && (
                      <span className="ml-2">
                        <DomainStatus value="disabled" />
                      </span>
                    )}
                  </Td>
                  <Td>
                    {s.connectionName} ·{' '}
                    <Mono>{[...s.roots.spaces, ...s.roots.pageIds].join(', ')}</Mono>
                    {s.roots.pageIds.length > 0 && (
                      <span className="text-xs text-muted">
                        {s.includeChildren ? ' · with child pages' : ' · root pages only'}
                      </span>
                    )}
                  </Td>
                  <Td>{s.authorityClass}</Td>
                  <Td>{s.classification}</Td>
                  <Td className="text-right tabular-nums">{s.documents}</Td>
                  <Td className="text-muted">{formatTime(s.lastSyncAt)}</Td>
                  {manage && (
                    <Td className="whitespace-nowrap">
                      <div className="flex gap-1.5">
                        <Button onClick={() => startEdit(s)} aria-label={`Edit ${s.name}`}>
                          Edit
                        </Button>
                        <Button onClick={() => sync.mutate({ id: s.id, fullResync: false })}>
                          <RefreshCw className="size-3.5" aria-hidden /> Sync
                        </Button>
                        <Button onClick={() => sync.mutate({ id: s.id, fullResync: true })}>
                          Full resync
                        </Button>
                      </div>
                    </Td>
                  )}
                </tr>
              ))}
              {sources.data?.items.length === 0 && (
                <tr>
                  <Td colSpan={manage ? 7 : 6} className="text-muted">
                    No knowledge sources yet.
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
      {selected && <SourceDetail id={selected} manage={manage} />}
    </div>
  );
}

function SourceDetail({ id, manage }: { id: string; manage: boolean }) {
  const runs = useQuery({
    queryKey: ['k-runs', id],
    queryFn: () => api<{ items: IngestionRunView[] }>(`/api/v1/knowledge/sources/${id}/runs`),
    refetchInterval: 5_000,
  });
  const docs = useQuery({
    queryKey: ['k-docs', id],
    queryFn: () =>
      api<{
        items: {
          id: string;
          externalId: string;
          title: string;
          version: string;
          status: string;
          restricted: boolean;
          synthetic: boolean;
          chunks: number;
          knowledgeItems: number;
          ingestedAt: string;
        }[];
      }>(`/api/v1/knowledge/sources/${id}/documents`),
    refetchInterval: 10_000,
  });
  const [queued, setQueued] = useState<string | null>(null);
  const extract = useMutation({
    mutationFn: (docId: string) =>
      api(`/api/v1/knowledge/documents/${docId}/extract`, { method: 'POST', body: {} }),
    onSuccess: (_d, docId) => setQueued(docId),
  });
  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Sync runs</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <thead>
              <tr>
                <Th>Started</Th>
                <Th>Trigger</Th>
                <Th>Status</Th>
                <Th>Counts</Th>
                <Th>Error</Th>
              </tr>
            </thead>
            <tbody>
              {(runs.data?.items ?? []).map((r) => (
                <tr key={r.id}>
                  <Td className="text-muted">{formatTime(r.startedAt)}</Td>
                  <Td>{r.trigger}</Td>
                  <Td>
                    <DomainStatus value={r.status === 'partial' ? 'degraded' : r.status} />
                  </Td>
                  <Td>
                    <Mono>
                      {Object.entries(r.counts)
                        .map(([k, v]) => `${k} ${v}`)
                        .join(' · ')}
                    </Mono>
                  </Td>
                  <Td>{r.error ? `${r.error.code}: ${r.error.message}` : '—'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {extract.isError && (
            <div className="p-4">
              <ErrorNote error={extract.error} />
            </div>
          )}
          {queued && (
            <p role="status" className="px-4 pt-3 text-xs text-muted">
              Extraction queued; candidates appear under Review when it finishes.
            </p>
          )}
          <Table>
            <thead>
              <tr>
                <Th>Page</Th>
                <Th>Version</Th>
                <Th>Status</Th>
                <Th className="text-right">Chunks</Th>
                <Th className="text-right">Knowledge</Th>
                <Th>Ingested</Th>
                {manage && <Th>Knowledge</Th>}
              </tr>
            </thead>
            <tbody>
              {(docs.data?.items ?? []).map((d) => (
                <tr key={d.id}>
                  <Td>
                    {d.title}
                    <p className="text-xs text-muted">
                      <Mono>{d.externalId}</Mono>
                      {d.synthetic && ' · SYNTHETIC'}
                      {d.restricted && ' · restricted in source (excluded from search)'}
                    </p>
                  </Td>
                  <Td className="tabular-nums">{d.version}</Td>
                  <Td>
                    <DomainStatus value={d.status === 'current' ? 'active' : d.status} />
                  </Td>
                  <Td className="text-right tabular-nums">{d.chunks}</Td>
                  <Td className="text-right tabular-nums">{d.knowledgeItems}</Td>
                  <Td className="text-muted">{formatTime(d.ingestedAt)}</Td>
                  {manage && (
                    <Td>
                      {d.status === 'current' && !d.restricted && (
                        <Button onClick={() => extract.mutate(d.id)} disabled={extract.isPending}>
                          <Sparkles className="size-3.5" aria-hidden /> Extract
                        </Button>
                      )}
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
}

function SearchTab() {
  const [q, setQ] = useState('');
  const [asOf, setAsOf] = useState('');
  const search = useMutation({
    mutationFn: () =>
      api<SearchKnowledgeResult>('/api/v1/knowledge/search', {
        method: 'POST',
        body: { query: q, limit: 20, ...(asOf ? { asOf: new Date(asOf).toISOString() } : {}) },
      }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (q.trim().length >= 2) search.mutate();
  };
  const r = search.data;
  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <label className="min-w-64 flex-1 text-xs text-muted">
          Search
          <input
            className={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. partner API authentication"
          />
        </label>
        <label className="text-xs text-muted">
          As of (optional)
          <input
            className={input}
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </label>
        <Button type="submit" variant="primary" disabled={search.isPending}>
          <Search className="size-3.5" aria-hidden /> Search
        </Button>
      </form>
      {search.isError && <ErrorNote error={search.error} />}
      {r && (
        <p className="text-xs text-muted" role="status">
          {r.hits.length} result(s) · vector search {r.retrieval.vector}
          {r.retrieval.embeddingModel ? ` (${r.retrieval.embeddingModel})` : ''} · as of{' '}
          {formatTime(r.retrieval.asOf)}
        </p>
      )}
      <div className="space-y-3">
        {(r?.hits ?? []).map((h) => (
          <Card key={`${h.kind}:${h.id}`}>
            <CardBody className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{h.title}</span>
                {h.status && <DomainStatus value={h.status.toLowerCase()} />}
                <span className="text-xs text-muted">
                  {h.kind === 'item' ? h.itemType : 'document'} · {h.authorityClass} ·{' '}
                  {h.classification} · matched by {h.matchedBy.join(', ')}
                </span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{h.snippet}</p>
              {h.source && (
                <p className="text-xs text-muted">
                  Source: {h.source.kind} <Mono>{h.source.id}</Mono>
                  {h.source.version && <> v{h.source.version}</>}
                  {h.source.connection && <> via {h.source.connection}</>}
                  {h.source.locator?.section && <> · § {h.source.locator.section}</>}
                  {h.validTo && <> · valid until {formatTime(h.validTo)}</>}
                </p>
              )}
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}

interface ItemRow {
  id: string;
  type: string;
  title: string;
  summary: string;
  status: string;
  epistemic: string;
  authorityClass: string;
  classification: string;
  confidence: number;
  provenanceCount: number;
  recordedAt: string;
}

function ReviewTab() {
  const { can } = useSession();
  const review = can('knowledge.review');
  const qc = useQueryClient();
  const [status, setStatus] = useState('CANDIDATE');
  const [open, setOpen] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  // Validate/reject one or several candidates (each is audited as its own decision).
  const decide = useMutation({
    mutationFn: async (a: { ids: string[]; decision: 'validate' | 'reject' }) => {
      for (const id of a.ids)
        await api(`/api/v1/knowledge/items/${id}/review`, {
          method: 'POST',
          body: { decision: a.decision },
        });
    },
    onSettled: () => {
      setPicked(new Set());
      void qc.invalidateQueries({ queryKey: ['k-items'] });
    },
  });
  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const list = useQuery({
    queryKey: ['k-items', status],
    queryFn: () =>
      api<{ items: ItemRow[] }>(
        `/api/v1/knowledge/items?limit=100${status ? `&status=${status}` : ''}`,
      ),
  });
  const candidates = (list.data?.items ?? []).filter((i) => i.status === 'CANDIDATE');
  return (
    <div className="space-y-4">
      <label className="text-xs text-muted">
        Status{' '}
        <select
          className="ml-1 rounded-md border border-border bg-surface px-2 py-1 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {['CANDIDATE', 'VALIDATED', 'REJECTED', 'SUPERSEDED', 'DEPRECATED', ''].map((s) => (
            <option key={s} value={s}>
              {s || 'All'}
            </option>
          ))}
        </select>
      </label>
      {list.isError && <ErrorNote error={list.error} />}
      {review && candidates.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Button
            onClick={() =>
              setPicked(
                picked.size === candidates.length
                  ? new Set()
                  : new Set(candidates.map((i) => i.id)),
              )
            }
          >
            {picked.size === candidates.length ? 'Clear selection' : 'Select all candidates'}
          </Button>
          <Button
            variant="primary"
            disabled={picked.size === 0 || decide.isPending}
            onClick={() => decide.mutate({ ids: [...picked], decision: 'validate' })}
          >
            Validate selected ({picked.size})
          </Button>
          <Button
            variant="danger"
            disabled={picked.size === 0 || decide.isPending}
            onClick={() => decide.mutate({ ids: [...picked], decision: 'reject' })}
          >
            Reject selected
          </Button>
          {decide.isError && <ErrorNote error={decide.error} />}
        </div>
      )}
      <Card>
        <CardBody className="p-0">
          <Table>
            <thead>
              <tr>
                <Th>Knowledge</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Origin</Th>
                <Th className="text-right">Evidence</Th>
                <Th>Recorded</Th>
              </tr>
            </thead>
            <tbody>
              {(list.data?.items ?? []).map((i) => (
                <Fragment key={i.id}>
                  <tr>
                    <Td>
                      {review && i.status === 'CANDIDATE' && (
                        <input
                          type="checkbox"
                          aria-label={`Select ${i.title}`}
                          className="mr-2 align-middle"
                          checked={picked.has(i.id)}
                          onChange={() => toggle(i.id)}
                        />
                      )}
                      <button
                        type="button"
                        className="text-left text-accent hover:underline"
                        onClick={() => setOpen(open === i.id ? null : i.id)}
                      >
                        {i.title}
                      </button>
                      <p className="text-xs text-muted">{i.summary}</p>
                    </Td>
                    <Td>
                      <Mono>{i.type}</Mono>
                    </Td>
                    <Td>
                      <DomainStatus value={i.status.toLowerCase()} />
                    </Td>
                    <Td className="text-xs">
                      {i.epistemic.toLowerCase()} · {i.authorityClass} ·{' '}
                      {Math.round(i.confidence * 100)}%
                    </Td>
                    <Td className="text-right tabular-nums">{i.provenanceCount}</Td>
                    <Td className="text-muted">
                      {formatTime(i.recordedAt)}
                      {review && i.status === 'CANDIDATE' && (
                        <span className="mt-1 flex gap-1">
                          <Button
                            className="px-2 py-1 text-xs"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ ids: [i.id], decision: 'validate' })}
                          >
                            Validate
                          </Button>
                          <Button
                            variant="danger"
                            className="px-2 py-1 text-xs"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ ids: [i.id], decision: 'reject' })}
                          >
                            Reject
                          </Button>
                        </span>
                      )}
                    </Td>
                  </tr>
                  {open === i.id && (
                    <tr>
                      <Td colSpan={6} className="bg-surface-muted">
                        <ItemDetail
                          id={i.id}
                          canReview={review}
                          onChanged={() => void qc.invalidateQueries({ queryKey: ['k-items'] })}
                        />
                      </Td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {list.data?.items.length === 0 && (
                <tr>
                  <Td colSpan={6} className="text-muted">
                    Nothing here.
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
}

function ItemDetail({
  id,
  canReview,
  onChanged,
}: {
  id: string;
  canReview: boolean;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const item = useQuery({
    queryKey: ['k-item', id],
    queryFn: () => api<KnowledgeItemView>(`/api/v1/knowledge/items/${id}`),
  });
  const [note, setNote] = useState('');
  const [replacement, setReplacement] = useState('');
  const act = useMutation({
    mutationFn: (a: { path: string; body: unknown }) =>
      api(a.path, { method: 'POST', body: a.body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['k-item', id] });
      onChanged();
    },
  });
  const v = item.data;
  if (item.isError) return <ErrorNote error={item.error} />;
  if (!v) return null;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{v.title}</CardTitle>
          <DomainStatus value={v.status.toLowerCase()} />
          <span className="text-xs text-muted">
            {v.type} ({v.kind.toLowerCase()}) · {v.epistemic.toLowerCase()} · {v.authorityClass} ·{' '}
            {v.classification}
          </span>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm">{v.summary}</p>
        <p className="text-xs text-muted">
          Valid {v.validFrom ? `from ${formatTime(v.validFrom)}` : 'from —'}{' '}
          {v.validTo ? `until ${formatTime(v.validTo)}` : ''}
          {v.validatedAt && <> · reviewed {formatTime(v.validatedAt)}</>}
          {v.reviewNote && <> · note: {v.reviewNote}</>}
        </p>
        <div>
          <p className="text-xs font-medium text-muted">
            Evidence (verbatim quotes from the source)
          </p>
          <ul className="mt-1 space-y-2">
            {v.provenance.map((p) => (
              <li key={p.id} className="rounded-md border border-border p-2 text-sm">
                {p.quote ? (
                  <blockquote className="border-l-2 border-accent pl-2 italic">
                    “{p.quote}”
                  </blockquote>
                ) : (
                  '—'
                )}
                <p className="mt-1 text-xs text-muted">
                  {p.method.toLowerCase()} · {p.documentTitle ?? 'no document'}
                  {p.documentVersion && <> v{p.documentVersion}</>}
                  {p.quoteHash && (
                    <>
                      {' '}
                      · <Mono>{p.quoteHash.slice(0, 12)}…</Mono>
                    </>
                  )}
                </p>
              </li>
            ))}
          </ul>
        </div>
        {v.edges.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted">Relationships</p>
            <ul className="mt-1 text-sm">
              {v.edges.map((e) => (
                <li key={e.id}>
                  {e.direction === 'out'
                    ? `→ ${e.predicate} → ${e.otherTitle}`
                    : `← ${e.predicate} ← ${e.otherTitle}`}{' '}
                  <span className="text-xs text-muted">({e.status.toLowerCase()})</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {canReview && (
          <div className="space-y-2">
            <label className="block text-xs text-muted">
              Review note
              <input className={input} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <div className="flex flex-wrap gap-1.5">
              {(v.status === 'CANDIDATE' || v.status === 'DISCOVERED') && (
                <>
                  <Button
                    variant="primary"
                    onClick={() =>
                      act.mutate({
                        path: `/api/v1/knowledge/items/${id}/review`,
                        body: { decision: 'validate', note },
                      })
                    }
                  >
                    Validate
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() =>
                      act.mutate({
                        path: `/api/v1/knowledge/items/${id}/review`,
                        body: { decision: 'reject', note },
                      })
                    }
                  >
                    Reject
                  </Button>
                </>
              )}
              {v.status === 'VALIDATED' && (
                <>
                  <Button
                    onClick={() =>
                      act.mutate({
                        path: `/api/v1/knowledge/items/${id}/review`,
                        body: { decision: 'deprecate', note },
                      })
                    }
                  >
                    Deprecate
                  </Button>
                  <input
                    className="rounded-md border border-border bg-surface px-2 py-1 text-xs"
                    placeholder="Replacement item id"
                    value={replacement}
                    onChange={(e) => setReplacement(e.target.value)}
                  />
                  <Button
                    disabled={!replacement}
                    onClick={() =>
                      act.mutate({
                        path: `/api/v1/knowledge/items/${id}/supersede`,
                        body: { supersededById: replacement, note },
                      })
                    }
                  >
                    Supersede
                  </Button>
                </>
              )}
            </div>
            {act.isError && <ErrorNote error={act.error} />}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
